import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSonographers } from '../../../../rtk-apis/users/getSonographers';
import { setReportSonographer } from '../../../../rtk-apis/reports/setReportSonographer';
import { AppDispatch, RootState } from '../../../../store/store';
import { Failed } from '../../../../components';
import PersonPickerDialog from '../../../../components/PersonPickerDialog';
import { capitalizeFirstLetterOfEachWord } from '../../../../Utils/utils';
import { clearState } from '../../../../store/slices/reports/setReportSonographer';

type Sonographer = {
  _id: string;
  name?: string;
  email?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  /** Called with the chosen name so the report body's "Sonographer:" cell can be updated. */
  onSelected?: (sonographerName: string) => void;
};

/**
 * Choose who performed the scan, from the report body's "Sonographer:" row.
 *
 * Same picker as Assign Cardiologist (client request, 26 Aug: "how choosing cardiologist
 * works at bottom... can you do the same for sono?"), but a different operation: this
 * records an attribution, so it moves no worklist and changes no report status. It also
 * never touches `createdByName` — who created the report document stays true regardless
 * of who held the probe.
 */
const AssignSonographerModal = ({ isOpen, onClose, reportId, onSelected }: Props) => {
  const dispatch = useDispatch<AppDispatch>();

  const [openFailureModal, setOpenFailureModal] = useState(false);
  const [selectedSonographer, setSelectedSonographer] = useState('');
  const [message, setMessage] = useState('');
  const [sonographersList, setSonographersList] = useState<Sonographer[]>([]);
  const [search, setSearch] = useState('');

  const { data, loading: listLoading } = useSelector(
    (state: RootState) => state.getSonographersResponse
  );
  const { status, msg, loading } = useSelector(
    (state: RootState) => state.setReportSonographerResponse
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(getSonographers());
    } else {
      dispatch(clearState());
      setSonographersList([]);
      setSelectedSonographer('');
      setSearch('');
    }
  }, [isOpen]);

  useEffect(() => {
    setSonographersList(Array.isArray(data) ? data : []);
  }, [data]);

  // Success is silent — the chosen name lands in the report's "Sonographer:" cell, which
  // is the confirmation. Nikhil, 16 Sep: "remove the pop up which says assigned - its
  // unnecessary". Failure still shows; that one says something the user can't otherwise see.
  useEffect(() => {
    if (status === 'SUCCESS') {
      const chosen = sonographersList.find(s => s._id === selectedSonographer);
      const name = chosen ? capitalizeFirstLetterOfEachWord(chosen.name) : '';
      dispatch(clearState());
      onSelected?.(name);
      onClose();
    }
    if (status === 'FAILED') {
      setOpenFailureModal(true);
      setMessage(msg);
      const timer = setTimeout(() => {
        setOpenFailureModal(false);
        setMessage('');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const handleConfirm = () => {
    if (!selectedSonographer) {
      setOpenFailureModal(true);
      setMessage('Please select a sonographer first.');
      return;
    }
    dispatch(setReportSonographer({ reportId, userId: selectedSonographer }));
  };

  return (
    <>
      <PersonPickerDialog
        open={isOpen}
        onClose={onClose}
        title="Select Sonographer"
        subtitle="Select the sonographer who performed this study."
        people={sonographersList}
        loading={listLoading}
        emptyText="No sonographers found for this clinic."
        noMatchText={term => `No sonographer matches "${term}".`}
        search={search}
        onSearchChange={setSearch}
        selectedId={selectedSonographer}
        onSelect={setSelectedSonographer}
        onConfirm={handleConfirm}
        busy={loading}
        confirmLabel="Yes, Confirm"
        busyLabel="Saving…"
      />

      {openFailureModal && (
        <Failed
          isFailedModalOpen={openFailureModal}
          handleCloseModal={() => setOpenFailureModal(false)}
          title={message}
        />
      )}
    </>
  );
};

export default AssignSonographerModal;
